/*
 * Registration Component
 * 
 * Provides user registration interface with:
 * - Email/password registration
 * - Password confirmation validation
 * - Success feedback and auto-redirect
 * - Email pre-population from URL parameters (for invitations)
 * 
 * TO EDIT REGISTRATION FORM:
 * - Add/remove form fields in the form section
 * - Modify validation logic in handleSubmit
 * - Update success messaging and redirect timing
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { isValidEmail, APP_BRANDING } from '../../utils/config';

const Register = ({ onSwitchToLogin }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [verificationRequired, setVerificationRequired] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [isInvited, setIsInvited] = useState(false); // Track if user came from invitation
  const { register } = useAuth();

  // Check for email parameter in URL (from vehicle share invitations)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const emailParam = urlParams.get('email');
    
    if (emailParam && isValidEmail(emailParam)) {
      setFormData(prev => ({
        ...prev,
        email: emailParam
      }));
      setIsInvited(true);
      
      // Update URL to remove email parameter for cleaner appearance
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  /**
   * Handle form input changes
   * @param {Event} e - Input change event
   */
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  /**
   * Handle form submission and user registration
   * @param {Event} e - Form submission event
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate email format
    if (!isValidEmail(formData.email)) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    // Validate password confirmation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    // Validate password strength
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      setLoading(false);
      return;
    }

    // Attempt to register user
    const result = await register(formData.email, formData.password);
    if (result.success) {
      setSuccess(true);
      setRegisteredEmail(formData.email);
      
      // Check if verification is required
      if (result.verification_required) {
        setVerificationRequired(true);
      } else {
        // Old flow - automatically switch to login after 3 seconds
        setTimeout(() => onSwitchToLogin(), 3000);
      }
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  // Success state display
  if (success) {
    if (verificationRequired) {
      // New email verification flow
      return (
        <div className="min-h-screen bg-gradient-to-br from-blue-900 to-indigo-900 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md text-center">
            <div className="text-blue-600 text-6xl mb-4">📧</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Check Your Email!</h2>
            <p className="text-gray-600 mb-4">
              We've sent a verification link to:
            </p>
            <p className="text-lg font-semibold text-blue-600 mb-6">
              {registeredEmail}
            </p>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
              <h3 className="font-semibold text-blue-800 mb-2">Next Steps:</h3>
              <ol className="text-sm text-blue-700 space-y-1">
                <li>1. Check your email inbox</li>
                <li>2. Click the verification link</li>
                <li>3. Return here to log in</li>
                {isInvited && (
                  <li>4. Access your shared vehicles after login</li>
                )}
              </ol>
            </div>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-6">
              <p className="text-xs text-yellow-800">
                <strong>⚠️ Important:</strong> The verification link expires in 24 hours. 
                Check your spam folder if you don't see the email.
              </p>
            </div>

            {isInvited && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-6">
                <p className="text-sm text-green-800">
                  <strong>🎉 Great!</strong> After verification, you'll be able to access the vehicle that was shared with you.
                </p>
              </div>
            )}
            
            <button
              onClick={onSwitchToLogin}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors font-medium"
            >
              Go to Login Page
            </button>
          </div>
        </div>
      );
    } else {
      // Old success flow (backward compatibility)
      return (
        <div className="min-h-screen bg-gradient-to-br from-green-900 to-teal-900 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md text-center">
            <div className="text-green-600 text-6xl mb-4">✓</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Registration Successful!</h2>
            <p className="text-gray-600 mb-4">
              Your account has been created successfully.
              {isInvited && " You can now access shared vehicles!"}
            </p>
            <p className="text-sm text-gray-500">
              Redirecting to login in 3 seconds...
            </p>
            <div className="mt-4">
              <button
                onClick={onSwitchToLogin}
                className="text-green-600 hover:text-green-800 font-medium underline"
              >
                Go to Login Now
              </button>
            </div>
          </div>
        </div>
      );
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-900 to-teal-900 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-800">
            {isInvited ? `🚗 Join ${APP_BRANDING.name}` : 'Create Account'}
          </h2>
          <p className="text-gray-600 mt-2">
            {isInvited
              ? 'Complete registration to access shared vehicles'
              : `Join ${APP_BRANDING.name} today`
            }
          </p>
        </div>

        {/* Invitation Notice */}
        {isInvited && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <span className="text-blue-600 text-xl mr-3">🎉</span>
              <div>
                <h3 className="font-medium text-blue-800">You've been invited!</h3>
                <p className="text-sm text-blue-700 mt-1">
                  Someone has shared their vehicle with you. Complete your registration to start tracking!
                </p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Email Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
                isInvited ? 'bg-blue-50 border-blue-300' : ''
              }`}
              required
              placeholder="Enter your email"
              readOnly={isInvited} // Make read-only if pre-populated from invitation
            />
            {isInvited && (
              <p className="text-xs text-blue-600 mt-1">
                This email was pre-filled from your vehicle sharing invitation
              </p>
            )}
          </div>

          {/* Password Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              required
              placeholder="Create a password (min 6 characters)"
              minLength="6"
            />
          </div>

          {/* Confirm Password Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Confirm Password
            </label>
            <input
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              required
              placeholder="Confirm your password"
            />
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {/* Check if error indicates user already exists */}
              {(error.toLowerCase().includes('already') ||
                error.toLowerCase().includes('exists') ||
                error.toLowerCase().includes('registered')) ? (
                <div>
                  <p className="font-medium mb-2">This email is already registered!</p>
                  <p className="text-sm mb-2">
                    You can sign in using your existing credentials from Rideya.bz, or use the
                    <button
                      type="button"
                      onClick={onSwitchToLogin}
                      className="text-red-800 font-semibold underline mx-1 hover:text-red-900"
                    >
                      "Forgot your password?"
                    </button>
                    option on the Sign In page to reset it.
                  </p>
                  <button
                    type="button"
                    onClick={onSwitchToLogin}
                    className="mt-2 text-sm bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 transition-colors"
                  >
                    Go to Sign In
                  </button>
                </div>
              ) : (
                error
              )}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 transition-colors"
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Creating Account...
              </span>
            ) : (
              isInvited ? 'Complete Registration' : 'Create Account'
            )}
          </button>
        </form>

        {/* Navigation Links */}
        <div className="mt-6 text-center">
          <p className="text-gray-600">
            Already have an account?{' '}
            <button
              onClick={onSwitchToLogin}
              className="text-green-600 hover:text-green-800 font-medium underline"
            >
              Sign in
            </button>
          </p>
        </div>

        {/* Help Text */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            {isInvited 
              ? 'By completing registration, you agree to our terms and can access shared vehicles'
              : 'By creating an account, you can register vehicles and subscribe to bus notifications'
            }
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;